import React, { useEffect, useMemo, useState } from "react";
import { attendanceAnomalyApi, errorMessage } from "../adminApi";
import { Card, Table, Badge, Button, Loading, ErrorState, PageHeader, Avatar } from "../components/ui";
import { Icon } from "../components/icons";
import AttendanceViewModal, { useAttendanceDetail } from "../components/AttendanceDetailModal";
import { formatDate, formatTime, formatDateTime, attendanceStatusText, ANOMALY_HINT } from "../format";

/**
 * MONITOR ANOMALI ABSENSI (pengganti "Approval Absensi").
 * ============================================================================
 * READ-ONLY. Absensi TIDAK lagi butuh persetujuan — log dikirim apa adanya ke
 * Odoo dan ODOO yang memutuskan penanganan finalnya. Halaman ini hanya untuk
 * MEMANTAU record yang ditandai anomali (lupa check-in/out, double) + melihat
 * status sync terakhir dari Odoo. Tidak ada tombol approve/reject/edit. Satu-satunya
 * aksi: HAPUS absensi bermasalah (khusus SUPER_ADMIN/HR_ADMIN) dari modal detail.
 * ============================================================================
 */

const TYPES = [
  { key: "FORGOT_CHECKIN", dot: "bg-[#e0566b]" },
  { key: "FORGOT_CHECKOUT", dot: "bg-[#e6a93c]" },
  { key: "DOUBLE_CHECKIN", dot: "bg-[#8b6fd6]" },
  { key: "DOUBLE_CHECKOUT", dot: "bg-[#5b8def]" },
];
const SYNC_LABEL = { ok: "Berhasil", ok_with_errors: "Ada error", failed: "Gagal" };

const weekday = (v) => {
  const d = new Date(v);
  return isNaN(d) ? "" : d.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", weekday: "long" });
};

export default function AttendanceAnomalyPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sync, setSync] = useState(null); // status ingest terakhir dari Odoo (opsional)
  const [type, setType] = useState(""); // filter jenis anomali (client-side)
  const [search, setSearch] = useState("");
  const { selected, detail, open: openDetail, close: closeDetail } = useAttendanceDetail();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await attendanceAnomalyApi.list());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
    // Status sync bersifat opsional (hanya SUPER_ADMIN/HR_ADMIN). Diamkan bila 403.
    try {
      setSync(await attendanceAnomalyApi.syncStatus());
    } catch {
      setSync(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!type || r.status === type) &&
        (!q || [r.employee_name, r.username, r.nik, r.department].some((v) => v && String(v).toLowerCase().includes(q)))
    );
  }, [rows, type, search]);

  const missing = <span className="text-[#c0737f] font-medium">tidak ada</span>;
  const columns = [
    {
      key: "emp",
      header: "Karyawan",
      render: (r) => (
        <div className="flex items-center gap-3 min-w-[220px]">
          <Avatar name={r.employee_name || r.username} size={34} />
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 leading-tight">{r.employee_name || r.username || String(r.user_id).slice(0, 8)}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {r.nik || r.username || ""}
              {r.department ? <span> · {r.department}</span> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "date",
      header: "Tanggal",
      render: (r) => (
        <div className="whitespace-nowrap">
          <div className="text-slate-700 font-medium">{formatDate(r.date)}</div>
          <div className="text-[11px] text-slate-400 capitalize">{weekday(r.date)}</div>
        </div>
      ),
    },
    {
      key: "time",
      header: "Masuk → Keluar",
      render: (r) => (
        <span className="whitespace-nowrap tabular-nums text-slate-700 font-medium">
          {r.check_in ? formatTime(r.check_in) : missing} <span className="text-slate-300">→</span>{" "}
          {r.check_out ? formatTime(r.check_out) : missing}
        </span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      render: (r) =>
        r.work_mode ? (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 capitalize">
            {r.work_mode}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "status",
      header: "Jenis Anomali",
      render: (r) => (
        <div className="max-w-sm">
          <Badge color="red">{attendanceStatusText(r.status)}</Badge>
          <div className="text-[11px] text-slate-400 mt-1 leading-snug">{ANOMALY_HINT[r.status] || ""}</div>
        </div>
      ),
    },
    {
      key: "act",
      header: "",
      cellClassName: "w-8",
      render: () => <Icon.ArrowRight size={16} className="text-slate-300" />,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Monitor Anomali Absensi"
        subtitle="Read-only. Log dikirim apa adanya ke Odoo; Odoo yang memutuskan penanganan final. Tidak ada approval."
        action={
          <Button variant="secondary" size="sm" onClick={load}>
            <Icon.Refresh size={15} /> Refresh
          </Button>
        }
      />

      {sync && (
        <div className="mb-3 shrink-0 bg-white rounded-2xl border border-slate-200/70 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <span className="h-8 w-8 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Icon.Refresh size={16} />
          </span>
          <div className="text-sm">
            <span className="text-slate-500">Sync terakhir dari Odoo </span>
            <span className="font-semibold text-slate-700">{formatDateTime(sync.created_at)}</span>
          </div>
          <Badge color={sync.status === "ok" ? "green" : sync.status === "ok_with_errors" ? "yellow" : "gray"}>
            {SYNC_LABEL[sync.status] || sync.status || "-"}
          </Badge>
          {sync.summary && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 sm:ml-auto">
              <span className="rounded-full bg-slate-100 px-2 py-0.5">total {sync.summary.total ?? 0}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5">baru {sync.summary.created ?? 0}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5">update {sync.summary.updated ?? 0}</span>
              {sync.summary.errors?.length ? (
                <span className="rounded-full bg-[#fce3e5] text-[#8f4657] px-2 py-0.5">error {sync.summary.errors.length}</span>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Filter jenis anomali (dengan jumlah) + pencarian */}
      <div className="flex flex-wrap items-center gap-2 mb-3 shrink-0">
        <TypeChip active={!type} onClick={() => setType("")} label="Semua" count={rows.length} />
        {TYPES.map((t) => (
          <TypeChip
            key={t.key}
            active={type === t.key}
            onClick={() => setType(type === t.key ? "" : t.key)}
            label={attendanceStatusText(t.key)}
            count={counts[t.key] || 0}
            dot={t.dot}
          />
        ))}
        <div className="h-10 rounded-full bg-white border border-slate-200/80 flex items-center gap-2 px-3.5 min-w-[220px] sm:ml-auto">
          <Icon.Search size={16} className="text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama / NIK / departemen…"
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading label="Memuat anomali absensi…" />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : (
          <Card className="flex-1 min-h-0 overflow-hidden">
            <Table
              columns={columns}
              rows={visible}
              onRowClick={openDetail}
              empty={rows.length ? "Tidak ada anomali untuk filter ini." : "Tidak ada anomali absensi 🎉"}
            />
          </Card>
        )}
      </div>

      <AttendanceViewModal
        open={!!selected}
        row={selected}
        detail={detail}
        onClose={closeDetail}
        onDeleted={() => {
          closeDetail();
          load();
        }}
        showRawLogs
      />
    </div>
  );
}

function TypeChip({ active, onClick, label, count, dot }) {
  return (
    <button
      onClick={onClick}
      className={`h-10 rounded-full border px-3.5 inline-flex items-center gap-2 text-[13px] font-medium transition ${
        active ? "bg-brand-600 border-brand-600 text-white shadow-sm" : "bg-white border-slate-200/80 text-slate-600 hover:text-slate-900"
      }`}
    >
      {dot && <span className={`h-2 w-2 rounded-full ${active ? "bg-white" : dot}`} />}
      {label}
      <span className={`min-w-[22px] px-1.5 rounded-full text-[11px] font-semibold ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
        {count}
      </span>
    </button>
  );
}
